import type { ImportStepKey } from '@/types/components/audiences/import-shell'

/* ------------------------------------------------------------------ */
/*  Route / pathname → step key mapping                              */
/*  Leaf module (no snapshot/build logic) to avoid bundler cycles /    */
/*  partial init where named exports from build-import-steps break.   */
/* ------------------------------------------------------------------ */

const SEGMENT_TO_STEP_KEY: Record<string, ImportStepKey> = {
  'workbook-assignment': 'workbook',
  'context-materials': 'context',
  'required-fields': 'identity',
  segmentation: 'segmentation',
  confirmation: 'confirm',
  status: 'creating',
}

const WIZARD_ROUTE_PREFIX = '/audiences/imports/'

/** Path segment under /audiences/imports/:sessionId/ for each step (for fallback routes). */
const STEP_KEY_TO_SEGMENT: Partial<Record<ImportStepKey, string>> = {
  workbook: 'workbook-assignment',
  context: 'context-materials',
  identity: 'required-fields',
  segmentation: 'segmentation',
  confirm: 'confirmation',
  creating: 'status',
}

/**
 * Return the route for a step when snapshot links are missing.
 * Used so clicking a completed step always navigates.
 */
export function getStepRouteFallback(
  sessionId: string,
  stepKey: ImportStepKey,
): string | null {
  if (stepKey === 'upload') return '/audiences/new'
  const segment = STEP_KEY_TO_SEGMENT[stepKey]
  if (!segment) return null
  return `${WIZARD_ROUTE_PREFIX}${sessionId}/${segment}`
}

/**
 * Derive the active step key from the current pathname.
 * Uses the last known wizard step segment in the path so step resolution stays
 * correct even when the URL contains extra trailing segments or query params.
 */
export function stepKeyFromPathname(pathname: string): ImportStepKey {
  const segments = pathname.split('/').filter(Boolean)
  for (let i = segments.length - 1; i >= 0; i--) {
    const key = SEGMENT_TO_STEP_KEY[segments[i]!]
    if (key) return key
  }
  return 'identity'
}

/**
 * Resolve a snapshot route string (e.g. snapshot.route or a link value)
 * to an ImportStepKey. Reuses the same segment lookup as `stepKeyFromPathname`
 * but accepts any route string — used by the single-page wizard to sync
 * local step state with backend snapshot updates.
 */
export function stepKeyFromRoute(
  route: string | null | undefined,
): ImportStepKey | null {
  if (!route) return null
  const cleaned = route.trim().split('?')[0]?.split('#')[0] ?? ''
  if (!cleaned) return null
  return stepKeyFromPathname(cleaned)
}

/**
 * True when `route` points to a step inside the audience-import wizard
 * (i.e. under `/audiences/imports/:sessionId/...`).
 */
export function isWizardInternalRoute(route: string): boolean {
  return route.startsWith(WIZARD_ROUTE_PREFIX)
}
